import { useState, useCallback } from 'react'

import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'

import { RoomSession } from '../../models/Room'

import { ExportConfirmationDialog } from './ExportConfirmationDialog'
import { ExportType, getExportConfirmationEnabled } from './utils'

interface ExportConfirmationParams {
  roomSession?: RoomSession | null
  exportType?: ExportType
}

export const useExportConfirmation = ({ roomSession, exportType = 'integration' }: ExportConfirmationParams = {}) => {
  const accountId = useAppSelector(selectAccountId)

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [actionToConfirm, setActionToConfirm] = useState<((proceed: boolean) => void) | null>(null)

  const { data: account, isLoading: accountIsLoading } = useGetAccountQuery({ accountId })

  const confirmAction = useCallback(async (): Promise<boolean> => {
    const exportConfirmationEnabled = getExportConfirmationEnabled(exportType, account?.data.effectiveConfig?.complianceConfig?.exportConfirmation)

    if (!exportConfirmationEnabled && !roomSession?.isPrivate) {
      return Promise.resolve(true)
    }

    return new Promise<boolean>((resolve) => {
      setIsDialogOpen(true)
      setActionToConfirm(() => resolve)
    })
  }, [account, exportType, roomSession])

  const handleConfirm = () => {
    actionToConfirm?.(true)
    setIsDialogOpen(false)
    setActionToConfirm(null)
  }

  const handleClose = () => {
    actionToConfirm?.(false)
    setIsDialogOpen(false)
    setActionToConfirm(null)
  }

  const runMutationWithConfirmation = useCallback(
    async (mutationTrigger: () => Promise<any>) => {
      try {
        const shouldProces = await confirmAction()
        if (shouldProces) {
          await mutationTrigger()
        }
      } finally {
        setIsDialogOpen(false)
        setActionToConfirm(null)
      }
    },
    [confirmAction],
  )

  const ConfirmationModal = useCallback(
    () => (
      <ExportConfirmationDialog
        isOpen={isDialogOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        account={account?.data}
        session={roomSession}
        exportType={exportType}
      />
    ),
    [isDialogOpen, account, exportType],
  )

  return {
    runMutationWithConfirmation,
    accountIsLoading,
    ConfirmationModal,
  }
}
